import { assign, setup } from "xstate";

import { PositiveInt } from "@src/shared/utils/types";

export type EncounterStatus =
  | "idle"
  | "connected"
  | "in_progress"
  | "finalizing"
  | "completed";

export type EncounterContext = {
  encounterId: PositiveInt | null;
  startedAtMs: number | null;
};

export type EncounterEvent =
  | { type: "ENCOUNTER_ID_CHANGED"; encounterId: PositiveInt | null }
  | { type: "TRANSCRIPTION_CREATED"; encounterId: PositiveInt }
  | { type: "SNAPSHOT_RECEIVED" }
  | { type: "TRANSCRIPT_SNAPSHOT_RECEIVED" }
  | { type: "TRANSCRIPT_ITEM_CREATED" }
  | { type: "FINALIZING" }
  | { type: "COMPLETED" }
  | { type: "REOPENED" }
  | { type: "SUPERSEDED"; newEncounterId: PositiveInt }
  | { type: "PATIENT_CLEARED" };

export const encounterMachine = setup({
  types: {
    context: {} as EncounterContext,
    events: {} as EncounterEvent,
  },
  actions: {
    assignIdFromEvent: assign({
      encounterId: ({ event }) => {
        if (
          event.type === "ENCOUNTER_ID_CHANGED" ||
          event.type === "TRANSCRIPTION_CREATED"
        ) {
          return event.encounterId;
        }
        return null;
      },
    }),
    assignNewIdFromSupersededEvent: assign({
      encounterId: ({ event }) => {
        if (event.type === "SUPERSEDED") return event.newEncounterId;
        return null;
      },
    }),
    clearEncounterId: assign({ encounterId: () => null }),
    recordStartedAt: assign({
      startedAtMs: () => Date.now(),
    }),
    clearStartedAt: assign({
      startedAtMs: () => null,
    }),
  },
}).createMachine({
  id: "encounter",
  initial: "idle",
  context: { encounterId: null, startedAtMs: null },
  on: {
    ENCOUNTER_ID_CHANGED: {
      target: ".idle",
      actions: ["assignIdFromEvent", "clearStartedAt"],
    },
    TRANSCRIPTION_CREATED: {
      target: ".idle",
      actions: ["assignIdFromEvent", "clearStartedAt"],
    },
    PATIENT_CLEARED: {
      target: ".idle",
      actions: ["clearEncounterId", "clearStartedAt"],
    },
  },
  states: {
    idle: {
      on: {
        SNAPSHOT_RECEIVED: { target: "connected" },
        TRANSCRIPT_SNAPSHOT_RECEIVED: { target: "in_progress" },
        TRANSCRIPT_ITEM_CREATED: { target: "in_progress" },
        FINALIZING: { target: "finalizing" },
        COMPLETED: { target: "completed" },
        REOPENED: { target: "in_progress" },
      },
    },
    connected: {
      entry: ["recordStartedAt"],
      on: {
        SNAPSHOT_RECEIVED: { target: "connected", reenter: false },
        TRANSCRIPT_SNAPSHOT_RECEIVED: { target: "in_progress" },
        TRANSCRIPT_ITEM_CREATED: { target: "in_progress" },
        FINALIZING: { target: "finalizing" },
        COMPLETED: { target: "completed" },
        REOPENED: { target: "in_progress" },
      },
    },
    in_progress: {
      on: {
        FINALIZING: { target: "finalizing" },
        COMPLETED: { target: "completed" },
        SUPERSEDED: {
          target: "connected",
          actions: ["assignNewIdFromSupersededEvent"],
        },
      },
    },
    finalizing: {
      on: {
        COMPLETED: { target: "completed" },
        REOPENED: { target: "in_progress" },
      },
    },
    completed: {
      entry: ["clearStartedAt"],
      on: {
        REOPENED: { target: "in_progress" },
        SUPERSEDED: {
          target: "connected",
          actions: ["assignNewIdFromSupersededEvent"],
        },
      },
    },
  },
});
